<template>
  <div class="fm-page">
    <header class="fm-head">
      <div class="fm-head__row">
        <h1>Inbox <small>sprechstunde@waizmann089.de</small></h1>
        <router-link class="btn btn--sm" :to="{ name: 'admin-termin' }">← Termin-Verwaltung</router-link>
      </div>
    </header>

    <div class="fm-shell">
      <!-- ============ ORDNER ============ -->
      <nav class="fm-folders">
        <button
          v-for="f in folders"
          :key="f.key"
          class="fm-folder"
          :class="{ 'fm-folder--active': folder === f.key }"
          @click="switchFolder(f.key)"
        >
          <span class="fm-folder__icon">{{ f.icon }}</span>
          <span class="fm-folder__label">{{ f.label }}</span>
          <span v-if="f.key === 'inbox' && unread" class="fm-folder__badge">{{ unread }}</span>
        </button>
      </nav>

      <!-- ============ LISTE ============ -->
      <section class="fm-list">
        <div class="fm-list__tools">
          <input
            v-model="q"
            type="search"
            placeholder="Suchen (Betreff, Absender, Text)…"
            @keyup.enter="loadList"
          />
          <button class="btn btn--sm" @click="loadList" title="Neu laden">⟳</button>
        </div>

        <p v-if="error" class="fm-empty">⚠️ {{ error }}</p>
        <p v-else-if="!mails.length" class="fm-empty">
          {{ loading ? "Lade…" : "Keine Mails hier." }}
        </p>

        <button
          v-for="m in mails"
          :key="m.mail_id"
          class="fm-row"
          :class="{ 'fm-row--unseen': !m.seen && m.direction === 0, 'fm-row--active': current && current.mail_id === m.mail_id }"
          @click="openMail(m.mail_id)"
        >
          <div class="fm-row__top">
            <span class="fm-row__from">{{ rowFrom(m) }}</span>
            <span class="fm-row__date">{{ fmtDate(m.date) }}</span>
          </div>
          <div class="fm-row__subject">
            <span v-if="m.starred" class="fm-star">★</span>
            {{ m.subject || "(kein Betreff)" }}
            <span v-if="m.attach_count" class="fm-clip">📎 {{ m.attach_count }}</span>
          </div>
          <div class="fm-row__snippet">{{ m.snippet }}</div>
        </button>
      </section>

      <!-- ============ DETAIL ============ -->
      <section class="fm-detail">
        <p v-if="!current" class="fm-empty fm-empty--detail">
          Mail links auswählen.
        </p>

        <template v-else>
          <div class="fm-mailhead">
            <div class="fm-mailhead__row">
              <h2>{{ current.subject || "(kein Betreff)" }}</h2>
              <div class="fm-mailactions">
                <button class="btn btn--sm" title="Antworten" @click="startReply">↩ Antworten</button>
                <button class="btn btn--sm" :title="current.starred ? 'Stern entfernen' : 'Favorit'"
                        @click="toggleFlag('mail_starred', current.starred ? 0 : 1)">
                  {{ current.starred ? "★" : "☆" }}
                </button>
                <button class="btn btn--sm" title="Als ungelesen" @click="markUnseen">✉ ungelesen</button>
                <button v-if="!current.archived" class="btn btn--sm" title="Archivieren"
                        @click="toggleFlag('mail_archived', 1)">🗄 Archiv</button>
                <button v-else class="btn btn--sm" @click="toggleFlag('mail_archived', 0)">aus Archiv holen</button>
                <button v-if="!current.trashed" class="btn btn--sm btn--danger" title="Papierkorb"
                        @click="toggleFlag('mail_trashed', 1)">🗑</button>
                <button v-else class="btn btn--sm" @click="toggleFlag('mail_trashed', 0)">wiederherstellen</button>
              </div>
            </div>
            <p class="fm-mailhead__meta">
              <strong>{{ current.from_name || current.from_email }}</strong>
              &lt;{{ current.from_email }}&gt;
              · {{ fmtDateTime(current.date) }}<br />
              <span v-if="current.to.length">an {{ current.to.map(r => r.email).join(", ") }}</span>
              <span v-if="current.cc && current.cc.length"> · cc {{ current.cc.map(r => r.email).join(", ") }}</span>
            </p>

            <div v-if="attachments.length" class="fm-atts">
              <a
                v-for="a in attachments"
                :key="a.matt_id"
                class="fm-att"
                :href="attachmentUrl(a.matt_id)"
                target="_blank"
                rel="noopener"
                @click="checkAttachment($event, a)"
              >
                📎 {{ a.filename }}
                <small>{{ fmtSize(a.size) }}</small>
              </a>
            </div>
          </div>

          <!-- Body: HTML sandboxed (feste Mindesthöhe — Auto-Höhe geht im
               Sandbox-iframe nicht), sonst Plaintext als <pre> -->
          <iframe
            v-if="current.body_html"
            class="fm-body-frame"
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            :srcdoc="frameDoc"
          ></iframe>
          <pre v-else class="fm-body-text">{{ current.body_text }}</pre>

          <!-- ============ ANTWORTEN ============ -->
          <div v-if="reply.open" class="fm-reply">
            <h3>Antwort an {{ reply.to }}</h3>
            <label class="fm-field">
              <span>Betreff</span>
              <input v-model="reply.subject" type="text" />
            </label>
            <label class="fm-field">
              <span>Nachricht</span>
              <textarea v-model="reply.body" rows="8"></textarea>
            </label>
            <div class="fm-reply__actions">
              <button class="btn btn--primary" :disabled="reply.sending || !reply.body.trim()" @click="sendReply">
                {{ reply.sending ? "Wird gesendet…" : "✉️ Senden" }}
              </button>
              <button class="btn" @click="reply.open = false">Verwerfen</button>
            </div>
          </div>
        </template>
      </section>
    </div>

    <div v-if="flash" class="admin-flash">{{ flash }}</div>
  </div>
</template>

<script>
import BaseCalls from "@/services/BaseCalls.js";

export default {
  name: "AdminMailIndex",
  data() {
    return {
      folders: [
        { key: "inbox",      label: "Inbox",         icon: "📥" },
        { key: "unanswered", label: "Unbeantwortet", icon: "❗" },
        { key: "sent",       label: "Gesendet",      icon: "📤" },
        { key: "drafts",     label: "Entwürfe",      icon: "📝" },
        { key: "starred",    label: "Favoriten",     icon: "★" },
        { key: "archive",    label: "Archiv",        icon: "🗄" },
        { key: "trash",      label: "Papierkorb",    icon: "🗑" },
      ],
      folder: "inbox",
      q: "",
      mails: [],
      unread: 0,
      loading: false,
      error: "",
      current: null,
      attachments: [],
      reply: { open: false, to: "", subject: "", body: "", sending: false },
      flash: "",
      timer: null,
    };
  },
  computed: {
    frameDoc() {
      if (!this.current || !this.current.body_html) return "";
      // <base target=_blank>: Links öffnen im neuen Tab statt im Sandbox-Frame.
      return '<base target="_blank"><style>body{font:15px/1.6 -apple-system,Segoe UI,Roboto,sans-serif;color:#222;margin:12px;word-break:break-word;}</style>'
        + this.current.body_html;
    },
  },
  mounted() {
    this.loadList();
    this.timer = setInterval(() => this.pollUnread(), 30000);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    fmtDate(v) {
      if (!v) return "";
      const d = new Date(String(v).replace(" ", "T"));
      if (isNaN(d)) return String(v);
      const today = new Date();
      const sameDay = d.toDateString() === today.toDateString();
      return sameDay
        ? d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
    },
    fmtDateTime(v) {
      if (!v) return "";
      const d = new Date(String(v).replace(" ", "T"));
      if (isNaN(d)) return String(v);
      return d.toLocaleDateString("de-DE") + ", "
        + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) + " Uhr";
    },
    fmtSize(bytes) {
      if (!bytes) return "";
      if (bytes > 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + " MB";
      return Math.max(1, Math.round(bytes / 1024)) + " KB";
    },
    rowFrom(m) {
      if (m.direction === 1 || m.folder === "Sent" || m.folder === "Draft") {
        const first = (m.to && m.to[0] && (m.to[0].name || m.to[0].email)) || "?";
        return "an " + first;
      }
      return m.from_name || m.from_email || "?";
    },
    showFlash(msg) {
      this.flash = msg;
      setTimeout(() => (this.flash = ""), 2600);
    },

    switchFolder(f) {
      this.folder = f;
      this.current = null;
      this.reply.open = false;
      this.loadList();
    },
    async loadList() {
      this.loading = true;
      try {
        const r = await BaseCalls.mailGet("list", "", {
          folder: this.folder, q: this.q || "", limit: 100,
        });
        if (r?.data?.success) {
          this.error = "";
          this.mails = r.data.mails || [];
          this.unread = r.data.unread ?? this.unread;
        } else {
          this.error = r?.data?.message || "Fehler beim Laden.";
        }
      } catch (_e) {
        this.error = "Netzwerk-/Serverfehler.";
      } finally {
        this.loading = false;
      }
    },
    async pollUnread() {
      try {
        const r = await BaseCalls.mailGet("unread");
        if (r?.data?.success) this.unread = r.data.unread;
      } catch (_e) { /* still */ }
    },
    async openMail(id) {
      const r = await BaseCalls.mailGet("mail", id);
      if (!r?.data?.success) {
        this.showFlash(r?.data?.message || "Mail konnte nicht geladen werden.");
        return;
      }
      this.current = r.data.mail;
      this.attachments = r.data.attachments || [];
      this.reply.open = false;
      // Liste lokal als gelesen markieren (Backend hat es schon getan).
      const row = this.mails.find((m) => m.mail_id === id);
      if (row && !row.seen) {
        row.seen = 1;
        this.unread = Math.max(0, this.unread - 1);
      }
    },

    async toggleFlag(flag, value) {
      if (!this.current) return;
      const r = await BaseCalls.mailPost({
        action: "setFlag", mail_id: this.current.mail_id, flag, value,
      });
      if (!r?.data?.success) {
        this.showFlash(r?.data?.message || "Fehler.");
        return;
      }
      const map = { mail_starred: "starred", mail_trashed: "trashed", mail_archived: "archived" };
      if (map[flag]) this.current[map[flag]] = value;
      if (flag === "mail_trashed" && value) this.showFlash("In den Papierkorb verschoben.");
      if (flag === "mail_archived" && value) this.showFlash("Archiviert.");
      this.loadList();
    },
    async markUnseen() {
      if (!this.current) return;
      await BaseCalls.mailPost({
        action: "setFlag", mail_id: this.current.mail_id, flag: "mail_seen", value: 0,
      });
      this.showFlash("Als ungelesen markiert.");
      this.current = null;
      this.loadList();
    },

    startReply() {
      const m = this.current;
      const quoted = (m.body_text || "")
        .split("\n").slice(0, 20).map((l) => "> " + l).join("\n");
      this.reply = {
        open: true,
        to: m.from_email,
        subject: /^re:/i.test(m.subject) ? m.subject : "Re: " + m.subject,
        body: `Hallo ${m.from_name || ""},\n\n\n\nViele Grüße\nIhre ZZV-Sprechstunde — Waizmann.089\n\n${quoted}`,
        sending: false,
      };
    },
    async sendReply() {
      this.reply.sending = true;
      const r = await BaseCalls.mailPost({
        action: "send",
        to: this.reply.to,
        subject: this.reply.subject,
        body: this.reply.body,
        reply_to_mail_id: this.current.mail_id,
      });
      this.reply.sending = false;
      if (r?.data?.success) {
        this.showFlash("Antwort gesendet.");
        this.reply.open = false;
        this.loadList();
      } else {
        this.showFlash(r?.data?.message || "Versand fehlgeschlagen.");
      }
    },

    attachmentUrl(mattId) {
      return BaseCalls.mailAttachmentUrl(mattId);
    },
    async checkAttachment(ev, a) {
      // FTP evtl. noch nicht konfiguriert: vorab prüfen und sauber melden,
      // statt den User auf eine JSON-Fehlerseite laufen zu lassen.
      ev.preventDefault();
      try {
        const resp = await fetch(this.attachmentUrl(a.matt_id), { credentials: "include" });
        if (!resp.ok) {
          const d = await resp.json().catch(() => ({}));
          this.showFlash(d.message || `Anhang nicht abrufbar (${resp.status}).`);
          return;
        }
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (_e) {
        this.showFlash("Anhang nicht abrufbar.");
      }
    },
  },
};
</script>

<style scoped>
/* Farben = Projekt-Design-Tokens (--wt-*, siehe App.vue) */
.fm-page { max-width: 1400px; margin: 0 auto; padding: 20px 16px 40px; }
.fm-head__row { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
.fm-head h1 { margin: 0 0 14px; color: var(--wt-ink); letter-spacing: -0.4px; }
.fm-head h1 small { font-size: .5em; color: var(--wt-muted); font-weight: 600; margin-left: 8px; }

.fm-shell {
  display: grid;
  grid-template-columns: 190px 380px 1fr;
  gap: 0;
  border: 1.5px solid var(--wt-blue-line);
  border-radius: 14px;
  overflow: hidden;
  background: var(--wt-bg);
  box-shadow: var(--wt-shadow);
  min-height: 70vh;
}
@media (max-width: 1000px) {
  .fm-shell { grid-template-columns: 1fr; }
  .fm-detail { border-top: 1px solid var(--wt-blue-line); }
}

/* ---- Ordner ---- */
.fm-folders {
  background: var(--wt-blue-soft);
  border-right: 1px solid var(--wt-blue-line);
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.fm-folder {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 12px; border: none; background: none; border-radius: 8px;
  cursor: pointer; font: inherit; font-size: .93em; text-align: left; color: var(--wt-text);
}
.fm-folder:hover { background: rgba(44, 91, 158, 0.1); }
.fm-folder--active { background: var(--wt-blue); color: #fff; font-weight: 700; }
.fm-folder__label { flex: 1; }
.fm-folder__badge {
  background: var(--wt-yellow); color: var(--wt-ink); border-radius: 999px;
  font-size: 11px; font-weight: 900; padding: 1px 7px;
}
.fm-folder--active .fm-folder__badge { background: var(--wt-yellow); color: var(--wt-ink); }

/* ---- Liste ---- */
.fm-list {
  border-right: 1px solid var(--wt-blue-line);
  overflow-y: auto;
  max-height: 80vh;
}
.fm-list__tools {
  display: flex; gap: 6px; padding: 10px;
  border-bottom: 1px solid var(--wt-blue-line); position: sticky; top: 0; background: #fff; z-index: 2;
}
.fm-list__tools input {
  flex: 1; padding: 7px 10px; border: 1px solid var(--wt-blue-line); border-radius: 999px; font: inherit; font-size: .9em;
}
.fm-list__tools input:focus { outline: none; border-color: var(--wt-blue); }
.fm-row {
  display: block; width: 100%; text-align: left;
  padding: 10px 12px; border: none; border-bottom: 1px solid var(--wt-blue-soft);
  background: #fff; cursor: pointer; font: inherit;
  border-left: 3px solid transparent;
}
.fm-row:hover { background: var(--wt-blue-soft); }
.fm-row--active { background: var(--wt-blue-soft); border-left-color: var(--wt-blue); }
.fm-row--unseen { border-left-color: var(--wt-yellow); }
.fm-row--unseen .fm-row__from, .fm-row--unseen .fm-row__subject { font-weight: 800; }
.fm-row__top { display: flex; justify-content: space-between; gap: 8px; }
.fm-row__from { font-size: .9em; color: var(--wt-blue-deep); }
.fm-row__date { font-size: .8em; color: var(--wt-muted); white-space: nowrap; }
.fm-row__subject { font-size: .95em; color: var(--wt-ink); margin: 2px 0; }
.fm-row__snippet {
  font-size: .84em; color: var(--wt-muted);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.fm-star { color: var(--wt-yellow); text-shadow: 0 0 1px var(--wt-ink); }
.fm-clip { font-size: .82em; color: var(--wt-muted); margin-left: 4px; }
.fm-empty { padding: 30px 16px; text-align: center; color: var(--wt-muted); }

/* ---- Detail ---- */
.fm-detail { overflow-y: auto; max-height: 80vh; padding: 0; }
.fm-empty--detail { padding-top: 80px; }
.fm-mailhead {
  padding: 16px 18px 12px;
  border-bottom: 1px solid var(--wt-blue-line);
  background: linear-gradient(180deg, var(--wt-blue-soft) 0%, #fff 100%);
}
.fm-mailhead__row { display: flex; justify-content: space-between; gap: 10px; align-items: flex-start; flex-wrap: wrap; }
.fm-mailhead h2 { margin: 0; font-size: 19px; letter-spacing: -0.2px; color: var(--wt-ink); }
.fm-mailactions { display: flex; gap: 5px; flex-wrap: wrap; }
.fm-mailhead__meta { margin: 8px 0 0; font-size: .88em; color: var(--wt-text); line-height: 1.6; }
.fm-mailhead__meta strong { color: var(--wt-blue-deep); }

.fm-atts { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
.fm-att {
  display: inline-flex; align-items: center; gap: 6px;
  background: #fff; border: 1px solid var(--wt-blue-line); border-radius: 999px;
  padding: 5px 12px; font-size: .87em; color: var(--wt-blue-deep); text-decoration: none; font-weight: 600;
}
.fm-att:hover { border-color: var(--wt-blue); background: var(--wt-blue-soft); }
.fm-att small { color: var(--wt-muted); font-weight: 400; }

/* Sandbox-iframe: Auto-Höhe unmöglich -> feste Mindesthöhe (Bauplan). */
.fm-body-frame { width: 100%; min-height: 800px; border: none; display: block; }
.fm-body-text {
  padding: 16px 18px; margin: 0; font: 15px/1.6 inherit;
  white-space: pre-wrap; word-break: break-word; color: var(--wt-text);
}

/* ---- Antwort ---- */
.fm-reply {
  border-top: 2px solid var(--wt-blue-line); padding: 16px 18px; background: var(--wt-blue-soft);
}
.fm-reply h3 { margin: 0 0 12px; font-size: 15px; color: var(--wt-ink); }
.fm-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 10px; font-size: 13px; font-weight: 700; color: var(--wt-text); }
.fm-field input, .fm-field textarea {
  font: inherit; font-weight: 400; padding: 9px 11px;
  border: 1.5px solid var(--wt-blue-line); border-radius: 8px; background: #fff;
}
.fm-field input:focus, .fm-field textarea:focus { outline: none; border-color: var(--wt-blue); }
.fm-reply__actions { display: flex; gap: 8px; }

/* ---- Buttons ---- */
.btn {
  padding: 6px 12px; border: 1px solid var(--wt-blue-line); background: #fff; border-radius: 8px;
  cursor: pointer; font: inherit; font-size: .9em; text-decoration: none; color: var(--wt-text);
  display: inline-flex; align-items: center; gap: 5px;
}
.btn:hover:not(:disabled) { border-color: var(--wt-blue); color: var(--wt-blue-deep); }
.btn:disabled { opacity: .5; cursor: not-allowed; }
.btn--sm { padding: 4px 9px; font-size: .85em; }
.btn--primary {
  background: var(--wt-blue); color: #fff; border-color: var(--wt-blue);
  border-left: 4px solid var(--wt-yellow); font-weight: 700;
}
.btn--primary:hover:not(:disabled) { background: var(--wt-blue-deep); color: #fff; }
.btn--danger { color: var(--wt-red); border-color: #f3cdc7; }

.admin-flash {
  position: fixed; bottom: 24px; right: 24px;
  background: var(--wt-blue-deep); color: white; padding: 10px 16px; border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0,0,0,.15); z-index: 50;
}
</style>
